<script setup lang="ts">
import { formatDate, parseToISODate, toLocalISODate } from '@/utils/format';

const props = withDefaults(
  defineProps<{
    modelValue?: string | null;
    label?: string;
    placeholder?: string;
    density?: 'default' | 'comfortable' | 'compact';
    hideDetails?: boolean | 'auto';
    variant?: 'outlined' | 'plain' | 'underlined' | 'filled' | 'solo' | 'solo-inverted' | 'solo-filled';
    disabled?: boolean;
    clearable?: boolean;
    error?: boolean;
    errorMessages?: string | string[];
  }>(),
  {
    modelValue: '',
    label: '',
    placeholder: 'DD/MM/YYYY',
    density: 'compact',
    hideDetails: true,
    variant: 'outlined',
    disabled: false,
    clearable: false,
    error: false,
    errorMessages: '',
  }
);

const emit = defineEmits<{
  'update:modelValue': [string];
  change: [string];
}>();

const { t, locale } = useI18n();

const text = ref('');
const syncing = ref(false);
const open = ref(false);
/** days | months | years */
const panel = ref<'days' | 'months' | 'years'>('days');
const viewYear = ref(new Date().getFullYear());
const viewMonth = ref(new Date().getMonth());
/** Tahun awal grid dekade (12 tahun) */
const yearGridStart = ref(Math.floor(new Date().getFullYear() / 12) * 12);

function isoOf(v: unknown) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) return toLocalISODate(v);
  return parseToISODate(v) || '';
}

function syncFromModel() {
  syncing.value = true;
  const iso = isoOf(props.modelValue);
  text.value = iso ? formatDate(iso) : '';
  if (iso) {
    const d = new Date(`${iso}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      viewYear.value = d.getFullYear();
      viewMonth.value = d.getMonth();
    }
  }
  nextTick(() => {
    syncing.value = false;
  });
}

watch(
  () => props.modelValue,
  () => {
    if (syncing.value) return;
    if (isoOf(text.value) === isoOf(props.modelValue)) return;
    syncFromModel();
  },
  { immediate: true }
);

function commitIso(raw: unknown) {
  const iso = isoOf(raw);
  text.value = iso ? formatDate(iso) : '';
  if (iso !== (props.modelValue || '')) {
    emit('update:modelValue', iso);
    emit('change', iso);
  }
  open.value = false;
}

const isoValue = computed(() => isoOf(props.modelValue));

const monthTitle = computed(() => {
  try {
    return new Intl.DateTimeFormat(locale.value === 'en' ? 'en-GB' : 'id-ID', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(viewYear.value, viewMonth.value, 1));
  } catch {
    return `${viewMonth.value + 1}/${viewYear.value}`;
  }
});

const headerTitle = computed(() => {
  if (panel.value === 'years') {
    return `${yearGridStart.value} – ${yearGridStart.value + 11}`;
  }
  if (panel.value === 'months') {
    return String(viewYear.value);
  }
  return monthTitle.value;
});

const monthLabels = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value === 'en' ? 'en-GB' : 'id-ID', { month: 'short' });
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(2024, m, 1)));
});

const yearCells = computed(() => {
  const nowY = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const y = yearGridStart.value + i;
    return {
      year: y,
      current: y === nowY,
      selected: y === viewYear.value,
    };
  });
});

const weekdayLabels = computed(() =>
  locale.value === 'en'
    ? ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
    : ['Sn', 'Sl', 'Ra', 'Ka', 'Ju', 'Sa', 'Mg']
);

type DayCell = { iso: string; day: number; outside: boolean; selected: boolean; today: boolean };

const dayCells = computed((): DayCell[] => {
  const y = viewYear.value;
  const m = viewMonth.value;
  const first = new Date(y, m, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - mondayOffset);
  const todayIso = toLocalISODate(new Date());
  const selectedIso = isoValue.value;
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = toLocalISODate(d);
    cells.push({
      iso,
      day: d.getDate(),
      outside: d.getMonth() !== m,
      selected: !!selectedIso && iso === selectedIso,
      today: iso === todayIso,
    });
  }
  return cells;
});

function shiftMonth(delta: number) {
  const d = new Date(viewYear.value, viewMonth.value + delta, 1);
  viewYear.value = d.getFullYear();
  viewMonth.value = d.getMonth();
}

function shiftNav(delta: number) {
  if (panel.value === 'years') {
    yearGridStart.value += delta * 12;
    return;
  }
  if (panel.value === 'months') {
    viewYear.value += delta;
    return;
  }
  shiftMonth(delta);
}

function cyclePanel() {
  if (panel.value === 'days') {
    yearGridStart.value = Math.floor(viewYear.value / 12) * 12;
    panel.value = 'years';
  } else if (panel.value === 'years') {
    panel.value = 'months';
  } else {
    panel.value = 'days';
  }
}

function pickYear(y: number) {
  viewYear.value = y;
  panel.value = 'months';
}

function pickMonth(m: number) {
  viewMonth.value = m;
  panel.value = 'days';
}

function openPicker() {
  if (props.disabled) return;
  if (!open.value) {
    panel.value = 'days';
    const iso = isoValue.value;
    if (iso) {
      const d = new Date(`${iso}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        viewYear.value = d.getFullYear();
        viewMonth.value = d.getMonth();
      }
    } else {
      const now = new Date();
      viewYear.value = now.getFullYear();
      viewMonth.value = now.getMonth();
    }
    yearGridStart.value = Math.floor(viewYear.value / 12) * 12;
  }
  open.value = !open.value;
}

function closePicker() {
  open.value = false;
  panel.value = 'days';
}

function pickDay(iso: string) {
  commitIso(iso);
}

function onClear(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  text.value = '';
  emit('update:modelValue', '');
  emit('change', '');
  closePicker();
}

function onKeydown(e: KeyboardEvent) {
  if (props.disabled) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    openPicker();
  } else if (e.key === 'Escape') {
    closePicker();
  }
}
</script>

<template>
  <div class="ddf" :class="{ 'ddf--open': open }">
    <button
      type="button"
      class="ddf__btn"
      :class="{
        'ddf__btn--open': open,
        'ddf__btn--error': error,
        'ddf__btn--compact': density === 'compact',
      }"
      :disabled="disabled"
      :aria-expanded="open"
      :aria-label="label || t('common.pickDate')"
      @click.stop="openPicker"
      @keydown="onKeydown"
    >
      <span class="ddf__cal" aria-hidden="true">
        <v-icon size="18">mdi-calendar-month</v-icon>
      </span>
      <span class="ddf__value" :class="{ 'ddf__value--empty': !text }">
        {{ text || placeholder }}
      </span>
      <span
        v-if="clearable && text"
        class="ddf__clear"
        role="button"
        tabindex="-1"
        @click="onClear"
      >
        <v-icon size="16">mdi-close-circle</v-icon>
      </span>
      <v-icon size="18" class="ddf__chev" aria-hidden="true">
        {{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </button>

    <div v-if="open" class="ddf-cal" role="dialog" aria-modal="true" @click.stop>
      <div class="ddf-cal__head">
        <button type="button" class="ddf-cal__nav" :aria-label="t('common.back')" @click="shiftNav(-1)">
          <v-icon size="18">mdi-chevron-left</v-icon>
        </button>
        <button type="button" class="ddf-cal__title" @click="cyclePanel">
          <span>{{ headerTitle }}</span>
          <v-icon size="16">{{ panel === 'days' ? 'mdi-menu-down' : 'mdi-menu-up' }}</v-icon>
        </button>
        <button type="button" class="ddf-cal__nav" :aria-label="t('common.next')" @click="shiftNav(1)">
          <v-icon size="18">mdi-chevron-right</v-icon>
        </button>
      </div>

      <!-- Pilih tahun -->
      <div v-if="panel === 'years'" class="ddf-cal__years">
        <button
          v-for="y in yearCells"
          :key="y.year"
          type="button"
          class="ddf-cal__chip"
          :class="{ selected: y.selected, current: y.current && !y.selected }"
          @click="pickYear(y.year)"
        >
          {{ y.year }}
        </button>
      </div>

      <!-- Pilih bulan -->
      <div v-else-if="panel === 'months'" class="ddf-cal__months">
        <button
          v-for="(label, m) in monthLabels"
          :key="m"
          type="button"
          class="ddf-cal__chip"
          :class="{ selected: m === viewMonth }"
          @click="pickMonth(m)"
        >
          {{ label }}
        </button>
      </div>

      <!-- Pilih hari -->
      <template v-else>
        <div class="ddf-cal__week">
          <span v-for="w in weekdayLabels" :key="w">{{ w }}</span>
        </div>
        <div class="ddf-cal__grid">
          <button
            v-for="cell in dayCells"
            :key="cell.iso"
            type="button"
            class="ddf-cal__day"
            :class="{
              outside: cell.outside,
              selected: cell.selected,
              today: cell.today && !cell.selected,
            }"
            @click="pickDay(cell.iso)"
          >
            {{ cell.day }}
          </button>
        </div>
      </template>

      <div class="ddf-cal__foot">
        <button type="button" class="ddf-cal__btn" @click="closePicker">{{ t('common.cancel') }}</button>
        <button type="button" class="ddf-cal__btn ddf-cal__btn--ok" @click="closePicker">
          {{ t('common.done') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ddf {
  position: relative;
  width: 100%;
  min-width: 9.5rem;
}
.ddf__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  min-width: 9.5rem;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}
.ddf__btn--compact {
  min-height: 38px;
}
.ddf__btn--open,
.ddf__btn:focus-visible {
  border-color: #0f766e;
  box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.18);
  outline: none;
}
.ddf__btn--error {
  border-color: #ef4444;
}
.ddf__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.ddf__cal {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f0fdfa;
  color: #0f766e;
}
.ddf__value {
  flex: 1 1 auto;
  min-width: 5.6rem;
  font-size: 13px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  color: #0f172a;
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
}
.ddf__value--empty {
  color: #94a3b8;
  font-weight: 600;
}
.ddf__clear {
  color: #94a3b8;
  display: inline-flex;
}
.ddf__chev {
  color: #94a3b8;
  flex: 0 0 auto;
}
.ddf--open .ddf__chev {
  color: #0f766e;
}

.ddf-cal {
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
  overflow: hidden;
}
.ddf-cal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 6px;
  background: linear-gradient(135deg, #f0fdfa 0%, #fff 70%);
}
.ddf-cal__month {
  font-size: 14px;
  font-weight: 750;
  color: #0f172a;
  text-align: center;
  flex: 1;
}
.ddf-cal__title {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 0;
  background: transparent;
  color: #0f172a;
  font-size: 14px;
  font-weight: 750;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  min-width: 0;
}
.ddf-cal__title:hover {
  background: #f0fdfa;
  color: #0f766e;
}
.ddf-cal__nav {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #f1f5f9;
  color: #334155;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.ddf-cal__years,
.ddf-cal__months {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding: 8px 10px 12px;
}
.ddf-cal__chip {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  padding: 12px 6px;
  cursor: pointer;
}
.ddf-cal__chip:hover {
  border-color: #99f6e4;
  background: #f0fdfa;
}
.ddf-cal__chip.current {
  box-shadow: inset 0 0 0 1.5px #0f766e;
  color: #0f766e;
}
.ddf-cal__chip.selected {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}
.ddf-cal__week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding: 0 8px 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  text-align: center;
}
.ddf-cal__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding: 0 8px 8px;
}
.ddf-cal__day {
  aspect-ratio: 1;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #0f172a;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}
.ddf-cal__day:hover {
  background: #f0fdfa;
}
.ddf-cal__day.outside {
  color: #cbd5e1;
}
.ddf-cal__day.today {
  box-shadow: inset 0 0 0 1.5px #0f766e;
  color: #0f766e;
}
.ddf-cal__day.selected {
  background: #0f766e;
  color: #fff;
}
.ddf-cal__foot {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 8px 10px 10px;
  border-top: 1px solid #e2e8f0;
}
.ddf-cal__btn {
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 12.5px;
  font-weight: 700;
  padding: 7px 11px;
  border-radius: 9px;
  cursor: pointer;
}
.ddf-cal__btn--ok {
  background: #0f766e;
  color: #fff;
}
</style>

<style>
html.jgi-dark .ddf__btn {
  background: #0a1020 !important;
  border-color: #3d5273 !important;
}
html.jgi-dark .ddf__value {
  color: #f1f5f9 !important;
}
html.jgi-dark .ddf-cal {
  background: #0f172a;
  border-color: #334155;
}
html.jgi-dark .ddf-cal__month,
html.jgi-dark .ddf-cal__title,
html.jgi-dark .ddf-cal__day {
  color: #f1f5f9;
}
html.jgi-dark .ddf-cal__title:hover {
  background: #042f2e;
  color: #5eead4;
}
html.jgi-dark .ddf-cal__chip {
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
}
html.jgi-dark .ddf-cal__chip.selected {
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
}
html.jgi-dark .ddf-cal__day.outside {
  color: #475569;
}
html.jgi-dark .ddf-cal__day.selected {
  background: #0f766e;
  color: #fff;
}
</style>
